import { useState, useRef, useCallback, useEffect } from 'react';
import { RecordingState } from '@/types/recording';

export function useMediaRecorder(deviceId?: string) {
  const [state, setState] = useState<RecordingState>({
    status: 'idle',
    duration: 0,
    audioBlob: null,
    audioSegments: [],
  });
  
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  const [error, setError] = useState<string | null>(null);
  const [permissionStatus, setPermissionStatus] = useState<'prompt' | 'granted' | 'denied' | 'unknown'>('unknown');
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingSegmentsRef = useRef<Blob[]>([]); // To store completed segments
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);
  const isStartingRef = useRef(false);
  const startTimeRef = useRef<number>(0); // Global start time
  const currentSegmentStartRef = useRef<number>(0);
  const totalDurationRef = useRef<number>(0); // Accumulated duration of previous segments
  const audioContextRef = useRef<AudioContext | null>(null); // Safari 心跳：保持音頻會話活躍
  const heartbeatIntervalRef = useRef<number | null>(null); // Safari 心跳定時器
  const mimeTypeRef = useRef<string>(''); // 存儲選定的 MIME Type，確保前後段一致

  // 檢查麥克風權限
  const checkPermission = useCallback(async () => {
    try {
      const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      setPermissionStatus(result.state as 'prompt' | 'granted' | 'denied');
      
      result.addEventListener('change', () => {
        setPermissionStatus(result.state as 'prompt' | 'granted' | 'denied');
      });
      
      return result.state;
    } catch {
      // 某些瀏覽器不支援 permissions API
      setPermissionStatus('unknown');
      return 'unknown';
    }
  }, []);

  // 準備麥克風流 (預熱)
  const prepareStream = useCallback(async (overrideDeviceId?: string) => {
    const targetDeviceId = overrideDeviceId || deviceId;
    setError(null);

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('瀏覽器安全限制：錄音功能需要 HTTPS 連接，或者您的瀏覽器不支援此功能。');
      }
      
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 1,
      };
      
      if (!isMobile) {
        audioConstraints.sampleRate = 48000;
      }
      
      if (targetDeviceId) {
        audioConstraints.deviceId = { exact: targetDeviceId };
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });
      
      setPermissionStatus('granted');
      streamRef.current = stream;

      // 在準備階段進行 300ms 預熱，避免錄音開始時的 Gain 跳變
      await new Promise(resolve => setTimeout(resolve, 300));
      return stream;
    } catch (err) {
      console.error('Failed to prepare stream:', err);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setPermissionStatus('denied');
          setError('麥克風權限被拒絕，請在瀏覽器設定中允許麥克風存取');
          throw err;
        }
        setError(err.message);
      }
      return null;
    }
  }, [deviceId]);

  // 開始錄音
  const startRecording = useCallback(async (overrideDeviceId?: string) => {
    // 如果已經在錄音，不重複開始
    if (isStartingRef.current || state.status === 'recording' || mediaRecorderRef.current?.state === 'recording') {
      return;
    }

    isStartingRef.current = true;

    try {
      // 1. 如果沒有預熱好的流，則現場準備
      let stream = streamRef.current;
      if (!stream || !stream.active) {
        stream = await prepareStream(overrideDeviceId);
      }
      
      if (!stream) throw new Error('無法獲取麥克風流');
      recordingSegmentsRef.current = [];
      totalDurationRef.current = 0;
      
      // 3. 選擇 MIME Type - iOS 必須優先使用 audio/mp4（AAC編碼）
      // 這是 iPhone 錄音能否成功的關鍵！WebM 在 iOS 上不支持或播放有問題
      let selectedMimeType = '';
      if (MediaRecorder.isTypeSupported('audio/mp4')) {
        selectedMimeType = 'audio/mp4'; // ✅ iOS 最愛 (AAC編碼)
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        selectedMimeType = 'audio/webm;codecs=opus'; // ✅ PC/Android 標準
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        selectedMimeType = 'audio/webm'; // ✅ 保底
      } else {
        throw new Error('此瀏覽器不支援任何錄音格式');
      }
      
      // 存儲 MIME Type 供 resumeRecording 使用，確保前後段格式一致
      mimeTypeRef.current = selectedMimeType;

      console.log('Using mimeType:', selectedMimeType);
      
      // MediaRecorder 配置 - 移動端使用預設比特率
      const recorderOptions: MediaRecorderOptions = { mimeType: selectedMimeType };
      if (!isMobile) {
        recorderOptions.audioBitsPerSecond = 128000; // 128kbps - 高保真錄音（僅桌面）
      }
      
      const mediaRecorder = new MediaRecorder(stream, recorderOptions);
      
      // Setup data handler for this specific recorder instance
      const localChunks: Blob[] = [];
      const segmentStartAt = Date.now();
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) localChunks.push(e.data);
      };
      
      mediaRecorder.onstop = () => {
         const segmentBlob = new Blob(localChunks, { type: selectedMimeType });
         if (segmentBlob.size > 0) {
             recordingSegmentsRef.current.push(segmentBlob);
             // Update total duration
             const segmentDuration = Date.now() - segmentStartAt;
             totalDurationRef.current += segmentDuration;
         }
      };

      mediaRecorderRef.current = mediaRecorder;
      currentSegmentStartRef.current = segmentStartAt;
      // 使用 timeslice 1000ms，讓數據分批到達 ondataavailable，防止長錄音導致內存峰值過高
      mediaRecorder.start(1000); 
      
      startTimeRef.current = Date.now();
      
      // Safari iOS 心跳機制：使用 Web Audio API 保持音頻會話活躍
      // Safari 會在約 8-10 秒無交互後暫停音頻，通過定期調用 resume() 防止這種情況
      if (isMobile && /Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent)) {
        console.log('Safari detected, starting audio context heartbeat...');
        
        // 創建並連接 AudioContext 到麥克風流
        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(stream);
        // 連接到一個無聲的目標（不實際播放）以保持上下文活躍
        const gainNode = audioContext.createGain();
        gainNode.gain.value = 0; // 靜音
        source.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        audioContextRef.current = audioContext;
        
        // 每 5 秒「心跳」一次，確保 AudioContext 保持 running 狀態
        heartbeatIntervalRef.current = window.setInterval(() => {
          if (audioContextRef.current && audioContextRef.current.state !== 'running') {
            console.log('Safari heartbeat: resuming audio context...');
            audioContextRef.current.resume();
          }
        }, 5000);
      }
      
      // Timer updates UI using net duration
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      timerRef.current = window.setInterval(() => {
        // We can't access state inside setInterval closure directly if we want latest
        // But we rely on Ref for calculation
        // However, we need to know if we are recording.
        // We can assume if this interval is running, we are recording?
        // But pause clears it.
        const currentSegmentDuration = Date.now() - currentSegmentStartRef.current;
        const total = totalDurationRef.current + currentSegmentDuration;
        setState(prev => ({ ...prev, duration: Math.floor(total / 1000) }));
      }, 1000);
      
      setState({ status: 'recording', duration: 0, audioBlob: null, audioSegments: [] });
      console.log('Recording started successfully');
      
    } catch (err) {
      console.error('Failed to start recording:', err);
      // ... error handling ...
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('麥克風權限被拒絕，請在瀏覽器設定中允許麥克風存取');
          setPermissionStatus('denied');
        } else if (err.name === 'NotFoundError') {
          setError('找不到麥克風設備');
        } else {
          setError(`錄音失敗: ${err.message}`);
        }
      } else {
        setError('Unknown error occurred');
      }
    } finally {
      isStartingRef.current = false;
    }
  }, [state.status, deviceId]);

  // 停止錄音
  // 停止錄音
  const stopRecording = useCallback(() => {
    // We should allow stopping if we are recording OR paused.
    // If paused, mediaRecorder state might be 'inactive' already, but we need to finalize the session.
    if (state.status === 'recording' || state.status === 'paused') {
      
      // Only call stop on the actual recorder if it's running
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop(); // Saves final segment
      }
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // 清理 Safari 心跳
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      
      streamRef.current?.getTracks().forEach(track => track.stop());
      
      setTimeout(() => {
          setState({
            status: 'stopped',
            duration: Math.floor(totalDurationRef.current / 1000),
            audioBlob: null, // No single blob yet
            audioSegments: [...recordingSegmentsRef.current]
          });
      }, 100);
    }
  }, [state.status]);

  // 暫停錄音
  const pauseRecording = useCallback(() => {
    console.log('[pauseRecording] Called');
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      try {
        console.log('[pauseRecording] Stopping current segment...');
        mediaRecorderRef.current.stop(); // This triggers onstop, saves segment
        // Do NOT stop stream tracks
        
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        
        // Update state to paused
        // Note: The onstop handler will update totalDurationRef
        setState(prev => ({ ...prev, status: 'paused', audioSegments: [...recordingSegmentsRef.current] })); 
      } catch (error) {
        console.error('[pauseRecording] Failed to pause:', error);
      }
    } else {
      console.warn('[pauseRecording] Cannot pause - mediaRecorder is null or not in recording state');
    }
  }, []);

  // 繼續錄音 - Start NEW Segment
  const resumeRecording = useCallback(() => {
    // Only resume if we have a stream and we are paused
    if (streamRef.current && state.status === 'paused') {
       console.log('[resumeRecording] Starting new segment...');
       
       // 使用之前存儲的 MIME Type，確保前後段格式一致
       const resumeMimeType = mimeTypeRef.current || 'audio/webm';
       
       // 檢測移動端
       const isMobileResume = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
       
       const resumeOptions: MediaRecorderOptions = { mimeType: resumeMimeType };
       if (!isMobileResume) {
         resumeOptions.audioBitsPerSecond = 128000;
       }

       const mediaRecorder = new MediaRecorder(streamRef.current, resumeOptions);
       
       const localChunks: Blob[] = [];
       const segmentStartAt = Date.now();
       mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) localChunks.push(e.data);
       };

       mediaRecorder.onstop = () => {
           const segmentBlob = new Blob(localChunks, { type: resumeMimeType });
           if (segmentBlob.size > 0) {
               recordingSegmentsRef.current.push(segmentBlob);
               const segmentDuration = Date.now() - segmentStartAt;
               totalDurationRef.current += segmentDuration;
           }
       };

       mediaRecorderRef.current = mediaRecorder;
       currentSegmentStartRef.current = segmentStartAt;
       mediaRecorder.start(1000); // 使用 timeslice 防止內存峰值

       // Timer
       if (timerRef.current) {
         clearInterval(timerRef.current);
       }
       timerRef.current = window.setInterval(() => {
           const currentSegmentDuration = Date.now() - currentSegmentStartRef.current;
           const total = totalDurationRef.current + currentSegmentDuration;
           setState(prev => ({ ...prev, duration: Math.floor(total / 1000) }));
       }, 1000);
       
       setState(prev => ({ ...prev, status: 'recording' }));
    }
  }, [state.status]); // Depend on status 'paused'

  // 重置
  const resetRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // 清理 Safari 心跳
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    streamRef.current?.getTracks().forEach(track => track.stop());
    mediaRecorderRef.current = null;
    streamRef.current = null;
    recordingSegmentsRef.current = [];
    totalDurationRef.current = 0;
    setError(null);
    setState({ status: 'idle', duration: 0, audioBlob: null, audioSegments: [] });
  }, []);

  // 清理
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
      streamRef.current?.getTracks().forEach(track => track.stop());
    };
  }, []);

  return {
    state,
    error,
    permissionStatus,
    prepareStream,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
    checkPermission,
    stream: streamRef.current // Export stream for AudioLevelMeter
  };
}
